// La page d'entrée : se connecter, ou créer un compte en six étapes.
//
// C'est le dernier écran migré, et il a été gardé pour la fin exprès : c'est le
// seul dont un défaut n'empêche pas de faire quelque chose, mais empêche
// d'ENTRER. Un membre qui ne peut pas s'inscrire ne peut rien signaler — il s'en
// va, et on ne le sait jamais.
//
// Trois choses en sortent intactes, parce qu'elles ont été payées cher :
//
// 1. **Le verrou d'envoi.** Désactiver le bouton ne suffit pas : sur un
//    téléphone, deux appuis séparés de quelques millisecondes déclenchent DEUX
//    soumissions avant que la première ait eu le temps de désactiver quoi que ce
//    soit — et Firebase, recevant deux créations simultanées, laisse passer les
//    deux. C'est ainsi que deux comptes ont existé pour une même adresse.
// 2. **L'inscription ne dépose plus aucun fichier** — depuis le 18 août 2026,
//    l'attestation de baptême a été retirée (le pourquoi est dans
//    `validerIdentite`, js/store.js). C'est aussi une contrainte de moins :
//    tant que personne n'est authentifié, les règles Storage refusent tout
//    dépôt, et il fallait donc créer le compte AVANT d'envoyer la photo.
//    Cet ordre délicat n'a plus lieu d'être.
// 3. **On vérifie chaque étape au moment de continuer**, jamais à la fin :
//    personne ne doit remplir cinq écrans pour apprendre que la première case
//    était fausse.

import { useEffect, useRef, useState } from "react";
import Store from "../../store.js";
import { APP } from "../../config.js";
import { toast, modale, fermerModale, esc } from "../../ui.js";
import { dateNaissanceSure, emailSur, motDePasseSur, telephoneSur, LIMITES } from "../../securite.js";
import ChampMotDePasse from "../ChampMotDePasse.jsx";

const DERNIERE = 6;   // la sixième est le récapitulatif, portée du téléphone le 24 août 2026

/** Messages Firebase → français lisible. */
function traduire(e) {
  const code = e?.code || "";
  if (code.includes("email-already-in-use")) return "Un compte existe déjà avec cet e-mail.";
  if (code.includes("invalid-email")) return "Adresse e-mail invalide.";
  if (code.includes("weak-password")) return "Mot de passe trop court (6 caractères minimum).";
  if (code.includes("invalid-credential") || code.includes("wrong-password") || code.includes("user-not-found"))
    return "E-mail ou mot de passe incorrect.";
  if (code.includes("too-many-requests")) return "Trop de tentatives. Réessayez dans quelques minutes.";
  if (code.includes("network")) return "Pas de connexion internet.";
  return e?.message || "Une erreur est survenue.";
}

/**
 * Ce qu'il faut avoir rempli pour passer à l'étape suivante.
 * Les contrôles viennent de `securite.js` : une seule règle, pas deux versions
 * qui finiraient par diverger.
 */
const CONTROLES = {
  1: (d) => (!d.prenom.trim() || !d.nomFamille.trim() ? "Indiquez votre prénom et votre nom." : null),
  2: (d) => {
    if (!d.genre) return "Choisissez votre genre.";
    try {
      dateNaissanceSure(d.dateNaissance);
      return null;
    } catch (e) {
      return e.message;
    }
  },
  3: (d) => {
    try {
      emailSur(d.email);
      motDePasseSur(d.mdp);
      telephoneSur(d.telephone, { requis: true });
    } catch (e) {
      return e.message;
    }
    if (!d.eglise.trim()) return "Indiquez votre église.";
    if (!d.ville.trim()) return "Indiquez votre ville.";
    return null;
  },
  4: (d) => {
    /* ⚠️ L'ATTESTATION DE BAPTÊME A ÉTÉ RETIRÉE LE 18 AOÛT 2026. Le pourquoi
       est écrit une seule fois, dans `validerIdentite` (js/store.js) — c'est
       là que la règle est appliquée pour les deux applications. Ici on ne fait
       que ne plus la demander.

       ⚠️ ÉGLISE ET DATE NE BLOQUENT PLUS. Elles restent affichées, et le
       responsable les lit ; mais quelqu'un qui ne se souvient pas de la date
       de son baptême ne doit pas rester à la porte pour autant. */
    if (d.baptise !== true && d.baptise !== false) return "Indiquez si vous êtes baptisé.";
    if (!d.connuPar.trim())
      return "Indiquez qui vous a fait connaître Koinonia.";
    return null;
  },
  5: (d) => (!d.accepte ? "Vous devez accepter la charte pour rejoindre la communauté." : null),
  6: () => null,   // le récapitulatif ne demande rien : il fait relire
};

const VIDE = {
  prenom: "", nomFamille: "", dateNaissance: "", genre: "",
  email: "", telephone: "", mdp: "", eglise: "", ville: "",
  // `baptise` vaut `null` tant qu'on n'a pas répondu : ni oui, ni non, ni
  // « on verra ». C'est ce qui permet d'exiger un vrai choix.
  baptise: null, connuPar: "", baptemeEglise: "", baptemeDate: "", accepte: false,
};

/* ------------------------------------------------------- mot de passe oublié */

/* Le texte est tenu ICI et recopié tel quel dans `koinonia-native/ecrans/
   Entree.jsx`. Il n'est pas partageable : le site parle en HTML, le téléphone
   en composants. Toute retouche à l'un doit être faite à l'autre. */
function confirmationOubli(adresse) {
  modale({
    titre: "C'est parti",
    corps: `
      <p>Si un compte existe pour <strong>${esc(adresse)}</strong>, un lien pour
      choisir un nouveau mot de passe vient d'y être envoyé.</p>
      <p class="petit texte-doux">Regardez aussi dans vos courriers indésirables.
      Si rien n'arrive d'ici quelques minutes, l'adresse n'est probablement pas
      celle de votre compte — essayez-en une autre, ou écrivez à
      <a href="mailto:contact@koinonia.bj">contact@koinonia.bj</a>.</p>`,
    actions: [{ label: "Fermer", classe: "btn--primaire" }],
  });
}


function ouvrirOubli(adresseConnue) {
  const { el } = modale({
    titre: "Mot de passe oublié",
    corps: `
      <p class="petit texte-doux">Indiquez votre adresse : nous vous enverrons un lien
      pour en choisir un nouveau. Votre mot de passe actuel ne peut pas vous être
      rappelé — il n'est conservé nulle part en clair, et c'est ce qui vous protège.</p>
      <label class="label">Adresse e-mail
        <input class="champ" id="oubli-email" type="email" autocomplete="email"
               placeholder="vous@exemple.com" value="${esc(adresseConnue || "")}">
      </label>`,
    actions: [
      { label: "Annuler" },
      {
        label: "Envoyer le lien",
        classe: "btn--primaire",
        onClick: async (fond) => {
          const adresse = fond.querySelector("#oubli-email").value.trim();
          if (!adresse) return toast("Indiquez votre adresse", "erreur");
          const bouton = fond.querySelector('[data-action="1"]');
          bouton.disabled = true;
          bouton.textContent = "Envoi…";
          try {
            await Store.auth.reinitialiserMotDePasse(adresse);
            fermerModale();
            /* ⚠️ LE MÊME TEXTE QUE SUR LE TÉLÉPHONE, MOT POUR MOT.
               Les deux applications partagent un cerveau ; elles doivent donc
               parler d'une seule voix — sinon on teste l'une en croyant avoir
               éprouvé l'autre. Demandé par Josaphat le 18 août 2026.

               ⚠️ ET ON NE PROMET PLUS L'ENVOI. « Le lien vient de partir »
               affirmait ce qu'aucune des deux applications ne peut savoir :
               Firebase accepte les adresses INEXISTANTES sans le dire (vérifié
               le 18 août 2026 — protection contre l'énumération des comptes).
               Quelqu'un qui se trompait d'adresse attendait donc un courriel
               qui ne partirait jamais, en tenant l'application pour cassée.

               ⚠️ ON NE RÉVÈLE TOUJOURS PAS SI LE COMPTE EXISTE. « Si un compte
               existe » reste en tête de phrase : ce formulaire ne doit pas
               servir à découvrir qui est membre de Koinonia — donc, ici, qui
               est chrétien. Ce qu'on ajoute, c'est la conduite à tenir, qui ne
               dit rien de personne. */
            confirmationOubli(adresse);
          } catch (e) {
            bouton.disabled = false;
            bouton.textContent = "Envoyer le lien";
            toast(traduire(e), "erreur");
          }
        },
      },
    ],
  });
  el.querySelector("#oubli-email").focus();
}

/* ------------------------------------------------------------------ connexion */

function FormConnexion() {
  const [email, setEmail] = useState("");
  const [mdp, setMdp] = useState("");
  const [enCours, setEnCours] = useState(false);

  const entrer = async (e) => {
    e.preventDefault();
    if (enCours) return;
    setEnCours(true);
    try {
      await Store.auth.signIn({ email: email.trim(), mdp });
    } catch (err) {
      toast(traduire(err), "erreur");
    }
    setEnCours(false);
  };

  return (
    <form className="formulaire" onSubmit={entrer}>
      <label className="label">
        E-mail
        <input
          className="champ"
          type="email"
          required
          autoComplete="email"
          placeholder="vous@exemple.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <label className="label">
        Mot de passe
        <ChampMotDePasse
          required
          minLength={6}
          autoComplete="current-password"
          placeholder="••••••••"
          valeur={mdp}
          onChange={setMdp}
        />
      </label>
      <button className="btn btn--primaire btn--bloc" type="submit" disabled={enCours}>
        {enCours ? "Un instant…" : "Entrer"}
      </button>
      <button type="button" className="lien-oubli" onClick={() => ouvrirOubli(email.trim())}>
        Mot de passe oublié ?
      </button>
    </form>
  );
}

/* --------------------------------------------------------------- inscription */

function Inscription() {
  const [etape, setEtape] = useState(1);
  const [d, setD] = useState(VIDE);
  const [enCours, setEnCours] = useState(false);
  // Le verrou d'envoi vit dans une RÉFÉRENCE, pas dans l'état : entre deux
  // appuis séparés de quelques millisecondes, un état n'a pas encore été
  // rafraîchi — c'est exactement ainsi que deux comptes ont été créés.
  const verrou = useRef(false);

  const maj = (champ) => (e) =>
    setD((v) => ({ ...v, [champ]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const suite = () => {
    const erreur = CONTROLES[etape](d);
    if (erreur) return toast(erreur, "erreur");
    setEtape((n) => n + 1);
  };

  const creer = async (e) => {
    e.preventDefault();
    if (verrou.current) return;
    verrou.current = true;
    // filet, si l'appel n'aboutit jamais
    const relache = setTimeout(() => (verrou.current = false), 15000);

    for (const n of [1, 2, 3, 4, 5, 6]) {
      const erreur = CONTROLES[n](d);
      if (erreur) {
        setEtape(n);
        verrou.current = false;
        clearTimeout(relache);
        return toast(erreur, "erreur");
      }
    }

    setEnCours(true);
    try {
      await Store.auth.signUp({
        prenom: d.prenom.trim(),
        nomFamille: d.nomFamille.trim(),
        dateNaissance: d.dateNaissance,
        genre: d.genre,
        telephone: (d.telephone || "").trim(),
        email: d.email.trim(),
        mdp: d.mdp,
        ville: (d.ville || "").trim(),
        eglise: (d.eglise || "").trim(),
        bapteme: {
          baptise: d.baptise,
          connuPar: (d.connuPar || "").trim(),
          // Vidées si la réponse est « non » : une église et une date de
          // baptême sous un « je ne suis pas baptisé » ne voudraient rien dire.
          eglise: d.baptise ? (d.baptemeEglise || "").trim() : "",
          date: d.baptise ? (d.baptemeDate || "") : "",
        },
      });
    } catch (err) {
      toast(traduire(err), "erreur");
    }
    setEnCours(false);
    verrou.current = false;
    clearTimeout(relache);
  };

  return (
    <form className="formulaire inscription" onSubmit={creer}>
      <div className="inscription__progression">
        <span style={{ width: `${(etape / DERNIERE) * 100}%` }} />
      </div>
      <p className="inscription__etape">Étape {etape} sur {DERNIERE}</p>

      <div className={`etape${etape === 1 ? " est-active" : ""}`}>
        <h3 className="etape__titre">Comment vous appelez-vous ?</h3>
        <div className="deux">
          <label className="label">
            Prénom
            <input className="champ" maxLength={40} autoComplete="given-name" placeholder="Grâce"
                   value={d.prenom} onChange={maj("prenom")} />
          </label>
          <label className="label">
            Nom
            <input className="champ" maxLength={60} autoComplete="family-name" placeholder="Adjovi"
                   value={d.nomFamille} onChange={maj("nomFamille")} />
          </label>
        </div>
      </div>

      <div className={`etape${etape === 2 ? " est-active" : ""}`}>
        <h3 className="etape__titre">Votre date de naissance</h3>
        <label className="label">
          Date de naissance
          <input className="champ" type="date" autoComplete="bday"
                 value={d.dateNaissance} onChange={maj("dateNaissance")} />
        </label>
        <p className="petit texte-doux">
          Elle ne s'affiche pas sur votre profil. Il faut avoir au moins 13 ans.
        </p>
        <label className="label">
          Genre
          <div className="filtres">
            {/* Deux choix, et deux seulement — « Autre » retiré le 14 août 2026.
                ⚠️ La valeur reste ACCEPTÉE par `genreSur` (voir `GENRES` dans
                securite.js) : un membre inscrit avant ce jour la porte peut-être,
                et la refuser bloquerait sa fiche sans qu'il comprenne pourquoi.
                On cesse de la proposer ; on n'efface personne. */}
            {[["femme", "Femme"], ["homme", "Homme"]].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`chip${d.genre === id ? " est-actif" : ""}`}
                onClick={() => setD((v) => ({ ...v, genre: id }))}
              >
                {label}
              </button>
            ))}
          </div>
        </label>
      </div>

      <div className={`etape${etape === 3 ? " est-active" : ""}`}>
        <h3 className="etape__titre">Comment vous joindre ?</h3>
        <p className="petit texte-doux">
          {/* ⚠️ « UN LIEN » A DISPARU LE 23 AOÛT 2026 — la confirmation se fait
              par un CODE à six chiffres. Annoncer un lien, c'est envoyer
              quelqu'un chercher dans sa boîte un message qui n'existe pas. */}
          Votre adresse e-mail sera à confirmer : un code à six chiffres vous sera envoyé. Le téléphone nous sert
          à vous joindre ; il n'est pas vérifié et n'est visible d'aucun membre.
        </p>
        <label className="label">
          E-mail
          <input className="champ" type="email" autoComplete="email" placeholder="vous@exemple.com"
                 value={d.email} onChange={maj("email")} />
        </label>
        <label className="label">
          Téléphone
          {/* ⚠️ LE BÉNIN EST PASSÉ À DIX CHIFFRES, COMMENÇANT PAR 01.
              L'exemple montrait encore l'ancien format à huit chiffres :
              quelqu'un qui le recopiait saisissait un numéro qui n'existe
              plus. Signalé par Josaphat le 24 août 2026. */}
          <input className="champ" type="tel" autoComplete="tel" placeholder="+229 01 97 00 00 00"
                 value={d.telephone} onChange={maj("telephone")} />
        </label>
        <label className="label">
          Mot de passe
          <ChampMotDePasse minLength={LIMITES.motDePasseMin} autoComplete="new-password"
          placeholder={`${LIMITES.motDePasseMin} caractères minimum`}
                           valeur={d.mdp} onChange={(v) => setD((x) => ({ ...x, mdp: v }))} />
        </label>
        <div className="deux">
          <label className="label">
            Votre église
            <input className="champ" maxLength={60} placeholder="Ex : ICC Parakou"
                   value={d.eglise} onChange={maj("eglise")} />
          </label>
          <label className="label">
            Votre ville
            <input className="champ" maxLength={60} placeholder="Ex : Parakou"
                   value={d.ville} onChange={maj("ville")} />
          </label>
        </div>
      </div>

      <div className={`etape${etape === 4 ? " est-active" : ""}`}>
        <h3 className="etape__titre">Votre parcours</h3>
        <p className="petit texte-doux">
          {/* ⚠️ « Nous », jamais « un responsable de l'église ». Koinonia est une
              plateforme, pas une paroisse : elle ne parle au nom d'aucune
              église et n'en invoque pas l'autorité. Correction demandée par
              Josaphat le 14 août 2026, elle vaut pour tous les textes. */}
          Koinonia rassemble des chrétiens qui se connaissent. Ces deux réponses nous aident
          à vous accueillir : nous les lisons <strong>sous {APP.delaiValidationHeures} heures</strong>,
          et elles ne sont jamais visibles des autres membres.
        </p>

        {/* ⚠️ DEUX BOUTONS, PAS UNE CASE À COCHER. Une case non cochée ne dit
            pas « non » — elle ne dit rien, et l'on ne saurait pas distinguer
            celui qui répond non de celui qui n'a pas vu la question. */}
        <fieldset className="choix-oui-non">
          <legend className="label">Êtes-vous baptisé ?</legend>
          <button
            type="button"
            className={`btn ${d.baptise === true ? "btn--primaire" : "btn--gris"}`}
            onClick={() => setD((v) => ({ ...v, baptise: true }))}
          >
            Oui
          </button>
          <button
            type="button"
            className={`btn ${d.baptise === false ? "btn--primaire" : "btn--gris"}`}
            onClick={() => setD((v) => ({ ...v, baptise: false }))}
          >
            Non
          </button>
        </fieldset>

        {/* Église et date n'ont de sens que sous un « oui ». Elles restent
            facultatives : on ne renvoie personne pour une date oubliée. */}
        {d.baptise === true && (
          <div className="deux">
            <label className="label">
              Église du baptême <span className="petit texte-doux">(facultatif)</span>
              <input className="champ" maxLength={60} placeholder="Ex : ICC Parakou"
                     value={d.baptemeEglise} onChange={maj("baptemeEglise")} />
            </label>
            <label className="label">
              Date du baptême <span className="petit texte-doux">(facultatif)</span>
              <input className="champ" type="date" value={d.baptemeDate} onChange={maj("baptemeDate")} />
            </label>
          </div>
        )}

        {/* ⚠️ CE NOM N'EST VÉRIFIÉ PAR AUCUNE MACHINE, et personne n'est
            prévenu. Il ne déclenche aucune confirmation : il sert au
            responsable, qui reconnaîtra — ou non — un lien réel. C'est le
            choix de Josaphat le 18 août 2026, et il vaut mieux qu'un
            automatisme : une machine ne sait pas ce qu'un nom veut dire dans
            une communauté. */}
        <label className="label">
          Comment avez-vous connu Koinonia ?
          <input className="champ" maxLength={60}
                 placeholder="Le nom de la personne qui vous en a parlé"
                 value={d.connuPar} onChange={maj("connuPar")} />
        </label>
      </div>

      <div className={`etape${etape === 5 ? " est-active" : ""}`}>
        <h3 className="etape__titre">La charte de la communauté</h3>
        <div className="charte">
          <ul>
            {APP.charte.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
          <label className="coche">
            <input type="checkbox" checked={d.accepte} onChange={maj("accepte")} /> J'accepte cette charte
          </label>
        </div>
        {/* ⚠️ « VOUS POURREZ LIRE LA COMMUNAUTÉ » EST DEVENU FAUX le 24 août
            2026 : un compte non validé ne voit plus rien, ni le fil ni les
            membres. Promettre une lecture qu'on refusera ensuite, c'est
            décevoir dès la première minute — et faire croire à une panne. */}
        <p className="petit texte-doux">
          Deux étapes suivront : confirmer votre adresse avec le code que nous vous
          enverrons, puis l'approbation d'un responsable — sous{" "}
          {APP.delaiValidationHeures} heures. Vous entrerez dans Koinonia à ce moment-là.
        </p>
      </div>

      {/* ⚠️ CET ÉCRAN EXISTE À CAUSE D'UNE ADRESSE MAL TAPÉE.
          Une faute d'un seul caractère dans l'e-mail, et le compte est
          mort-né : le code de confirmation part vers une boîte qui n'existe
          pas, et l'on ne peut plus jamais l'ouvrir. C'est arrivé la semaine du
          20 août, et nous avons cherché des heures avant de comprendre.
          Relire coûte cinq secondes ; ne pas relire a coûté un membre.

          ⚠️ L'ADRESSE EST SEULE ET EN GRAND. Noyée dans une liste, on la
          survole ; c'est précisément celle-là qu'il faut regarder deux fois.

          ⚠️ PORTÉ DU TÉLÉPHONE, qui l'avait depuis le 20 août. Les deux
          applications divergeaient encore sur ce point : cinq étapes ici, six
          là-bas. */}
      <div className={`etape${etape === 6 ? " est-active" : ""}`}>
        <h3 className="etape__titre">Vérifiez avant de créer</h3>

        <div className="recap-mail">
          <p className="recap-mail__quoi">Votre adresse e-mail</p>
          <p className="recap-mail__valeur">{d.email || "—"}</p>
          <p className="petit texte-doux">
            C'est là que partira votre code à six chiffres. Une seule lettre de
            travers et le compte ne pourra jamais être ouvert.
          </p>
        </div>

        <dl className="recap">
          <div><dt>Nom</dt><dd>{`${d.prenom} ${d.nomFamille}`.trim() || "—"}</dd></div>
          <div><dt>Naissance</dt><dd>{d.dateNaissance || "—"}</dd></div>
          <div><dt>Genre</dt><dd>{d.genre === "femme" ? "Femme" : d.genre === "homme" ? "Homme" : "—"}</dd></div>
          <div><dt>Téléphone</dt><dd>{d.telephone || "—"}</dd></div>
          <div><dt>Église</dt><dd>{d.eglise || "—"}</dd></div>
          <div><dt>Ville</dt><dd>{d.ville || "—"}</dd></div>
          <div><dt>Baptisé</dt><dd>{d.baptise === true ? "Oui" : d.baptise === false ? "Non" : "—"}</dd></div>
          <div><dt>Vous a fait connaître Koinonia</dt><dd>{d.connuPar || "—"}</dd></div>
        </dl>

        <p className="petit texte-doux">
          Une erreur ? Cliquez sur « Retour » pour revenir la corriger.
        </p>
      </div>

      <div className="inscription__boutons">
        {etape > 1 && (
          <button type="button" className="btn btn--gris" onClick={() => setEtape((n) => n - 1)}>
            Retour
          </button>
        )}
        {etape < DERNIERE ? (
          <button type="button" className="btn btn--primaire" onClick={suite}>
            Continuer
          </button>
        ) : (
          <button type="submit" className="btn btn--primaire" disabled={enCours}>
            {enCours ? "Un instant…" : "Créer mon compte"}
          </button>
        )}
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------- écran */

export default function Connexion() {
  const [onglet, setOnglet] = useState("connexion");

  return (
    <div className="accueil">
      {/* Page d'entrée volontairement nue, comme celle de Facebook : le nom, une
          ligne, et le formulaire. Détailler ce que fait l'application à
          quelqu'un qui n'y est pas encore ne sert à rien — il vient parce qu'on
          l'a invité, pas parce qu'on l'a convaincu. */}
      <section className="accueil__pitch">
        <span className="accueil__halo">
          <img className="logo__marque logo__marque--seule" src="icons/icon-192.png?v=2"
               alt="Koinonia" width="64" height="64" />
        </span>
        <h1 className="accueil__titre">Bienvenue sur {APP.nom}</h1>
      </section>

      <section className="carte carte--auth">
        <div className="onglets">
          <button
            className={`onglet${onglet === "connexion" ? " est-actif" : ""}`}
            onClick={() => setOnglet("connexion")}
          >
            Se connecter
          </button>
          <button
            className={`onglet${onglet === "inscription" ? " est-actif" : ""}`}
            onClick={() => setOnglet("inscription")}
          >
            Créer un compte
          </button>
        </div>

        {onglet === "connexion" ? <FormConnexion /> : <Inscription />}

        {Store.mode === "local" && (
          <div className="demo">
            <strong>Mode démonstration</strong>
            <p className="petit">
              Les données restent sur cet appareil (Firebase n'est pas encore configuré).
            </p>
            <button
              className="btn btn--fantome btn--petit"
              onClick={async () => {
                try {
                  await Store.auth.signIn({ email: "emmanuel@demo.app", mdp: "demo1234" });
                } catch (e) {
                  toast(e.message, "erreur");
                }
              }}
            >
              Entrer avec le compte de démo
            </button>
          </div>
        )}
      </section>

      {/* ⚠️ SOUS LE FORMULAIRE, PAS AU-DESSUS. Celui qui arrive ici vient pour
          entrer ou pour s'inscrire ; lui proposer un téléchargement de 46 Mo
          avant même qu'il ait un compte le ferait partir. La ligne est donc
          discrète et en dernier — elle sert celui qui la cherche.

          ⚠️ ET ELLE NE S'AFFICHE PAS DANS L'APPLICATION. Proposer d'installer
          l'application à quelqu'un qui est DÉJÀ dedans est le genre de détail
          qui fait douter du sérieux du reste. L'APK sert ses fichiers en
          `file://` ou `capacitor://` : c'est ce qui le distingue du site. */}
      {typeof location !== "undefined" && location.protocol.startsWith("http") && (
        <p className="petit texte-doux" style={{ textAlign: "center", marginTop: 18 }}>
          <a href="application.html">Installer l'application Android</a>
        </p>
      )}
    </div>
  );
}
